const MATCH_THRESHOLD = 0.55
const TIME_WINDOW_MINUTES = 60

export const normalizeTeam = (value = '') =>
  String(value)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\b(fc|sc|cf|afc|ac|as|ss|ssc|us|rc|rcd|cd|ud|ca|fk|sk|bk|if|sv|vfb|vfl|tsv|bsc|bsv|1\.\s*fc|1\.\s*fsv)\b/g, '')
    .replace(/\bunited\b/g, 'utd')
    .replace(/\bcity\b/g, 'cty')
    .replace(/[^a-z0-9 ]/g, '')
    .replace(/\s+/g, ' ')
    .trim()

export const diceCoefficient = (a = '', b = '') => {
  if (a === b) return 1
  if (a.length < 2 || b.length < 2) return 0

  const bigrams = (value) => {
    const counts = new Map()
    for (let index = 0; index < value.length - 1; index += 1) {
      const pair = value.slice(index, index + 2)
      counts.set(pair, (counts.get(pair) ?? 0) + 1)
    }
    return counts
  }

  const left = bigrams(a)
  const right = bigrams(b)
  let overlap = 0
  left.forEach((count, pair) => {
    overlap += Math.min(count, right.get(pair) ?? 0)
  })

  return (2 * overlap) / (a.length - 1 + b.length - 1)
}

export const isReversedTeamPair = (sourceHome, sourceAway, targetHome, targetAway) => {
  const directScore = (
    diceCoefficient(normalizeTeam(sourceHome), normalizeTeam(targetHome)) +
    diceCoefficient(normalizeTeam(sourceAway), normalizeTeam(targetAway))
  ) / 2
  const reversedScore = (
    diceCoefficient(normalizeTeam(sourceHome), normalizeTeam(targetAway)) +
    diceCoefficient(normalizeTeam(sourceAway), normalizeTeam(targetHome))
  ) / 2

  return reversedScore >= MATCH_THRESHOLD && reversedScore >= directScore
}

const toMinutes = (value) => {
  if (!value) return null
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null
  return date.getHours() * 60 + date.getMinutes()
}

const circularMinuteDifference = (left, right) => {
  const difference = Math.abs(left - right)
  return Math.min(difference, 1440 - difference)
}

const getTeamScore = (left, right) =>
  diceCoefficient(normalizeTeam(left), normalizeTeam(right))

const scorePair = (sourceEvent, targetEvent) => {
  const directHome = getTeamScore(sourceEvent.homeName, targetEvent.homeName)
  const directAway = getTeamScore(sourceEvent.awayName, targetEvent.awayName)
  const directScore = (directHome + directAway) / 2

  const reversedHome = getTeamScore(sourceEvent.homeName, targetEvent.awayName)
  const reversedAway = getTeamScore(sourceEvent.awayName, targetEvent.homeName)
  const reversedScore = (reversedHome + reversedAway) / 2

  const sourceMinutes = toMinutes(sourceEvent.kickoff)
  const targetMinutes = toMinutes(targetEvent.kickoff)
  const timeDifference =
    sourceMinutes == null || targetMinutes == null
      ? null
      : circularMinuteDifference(sourceMinutes, targetMinutes)

  return { directScore, reversedScore, timeDifference }
}

export const findAutoMatches = (sourceEvents = [], targetEvents = [], existingTargetIds = new Set()) => {
  const usedTargetIds = new Set(existingTargetIds)
  const matches = []
  let skippedReversed = 0

  sourceEvents.forEach((sourceEvent) => {
    if (!sourceEvent?.id) return

    let bestCandidate = null
    targetEvents.forEach((targetEvent) => {
      if (!targetEvent?.id || usedTargetIds.has(String(targetEvent.id))) return

      const score = scorePair(sourceEvent, targetEvent)
      if (
        score.reversedScore >= MATCH_THRESHOLD &&
        score.reversedScore >= score.directScore
      ) {
        skippedReversed += 1
        return
      }

      if (
        score.directScore < MATCH_THRESHOLD ||
        (score.timeDifference != null && score.timeDifference > TIME_WINDOW_MINUTES)
      ) {
        return
      }

      if (!bestCandidate || score.directScore > bestCandidate.score.directScore) {
        bestCandidate = { targetEvent, score }
      }
    })

    if (bestCandidate) {
      usedTargetIds.add(String(bestCandidate.targetEvent.id))
      matches.push({
        sourceEvent,
        targetEvent: bestCandidate.targetEvent,
        confidence: Math.round(bestCandidate.score.directScore * 100),
        timeDifference: bestCandidate.score.timeDifference,
      })
    }
  })

  return { matches, skippedReversed }
}

